import { IOrderType, ORDER_TYPES_MAP } from '@/const/orders.const';
import type { IRestaurantSettings } from '../api/services/restaurants.service';
import { OrderType } from '../enums/OrderEnums';
import type { IBranch } from '../interfaces/IBranch';
import type { IRestaurant } from '../interfaces/IRestaurant';
import type { ITable } from '../interfaces/ITable';
import { getEffectiveTier } from '@/utils/billing-entitlements.utils';

export function getAvailableOrderTypes({
  restaurant,
  branch,
  settings,
}: {
  restaurant: IRestaurant | null;
  branch: IBranch | null;
  settings: IRestaurantSettings | null;
}): IOrderType[] {
  const types: OrderType[] = [];

  if (
    !restaurant ||
    !branch ||
    getEffectiveTier(restaurant) === 0 ||
    !branch.allowOrders ||
    !settings
  ) {
    return [] as IOrderType[];
  }

  if (branch.tableOrders) {
    types.push(OrderType.Seat);
  }

  if (branch.delivery) {
    types.push(OrderType.Delivery);
  }

  if (branch.takeAway) {
    types.push(OrderType.Away);
  }

  return types.map((t) => ORDER_TYPES_MAP.get(t)!);
}

export function getEnabledOrderTypesFromBranch(
  branch: IBranch | null
): OrderType[] {
  if (!branch) return [];

  const types: OrderType[] = [];

  if (branch.tableOrders) {
    types.push(OrderType.Seat);
  }

  if (branch.takeAway) {
    types.push(OrderType.Away);
  }

  if (branch.delivery) {
    types.push(OrderType.Delivery);
  }

  return types;
}

export function getUsedMenuTypesByMenuId(
  settings: IRestaurantSettings | null,
  enabledTypes: OrderType[]
): Map<number | null, OrderType[]> {
  const map = new Map<number | null, OrderType[]>();

  for (const type of enabledTypes) {
    let menuId: number | null = null;

    // If multiple menus are not enabled, all order types use the default menu (null).
    if (settings?.multipleMenus) {
      if (type === OrderType.Seat) {
        menuId = settings.menu1Id ?? null;
      } else if (type === OrderType.Away) {
        menuId = settings.menu2Id ?? null;
      } else if (type === OrderType.Delivery) {
        menuId = settings.menu3Id ?? null;
      }
    }

    if (!map.has(menuId)) {
      map.set(menuId, []);
    }

    map.get(menuId)!.push(type);
  }

  return map;
}

export function formatMenuUsageLabel(types: OrderType[]): string {
  const order: OrderType[] = [OrderType.Seat, OrderType.Away, OrderType.Delivery];
  const tokensByType: Record<OrderType, string> = {
    [OrderType.Seat]: 'mesa',
    [OrderType.Away]: 'take away',
    [OrderType.Delivery]: 'delivery',
  };

  const tokens = order
    .filter((t) => types.includes(t))
    .map((t) => tokensByType[t]);

  if (!tokens.length) return 'Menú';
  if (tokens.length === 1) return `Menú ${tokens[0]}`;
  if (tokens.length === 2) return `Menú ${tokens[0]} y ${tokens[1]}`;

  const head = tokens.slice(0, -1).join(', ');
  const tail = tokens[tokens.length - 1];
  return `Menú ${head} y ${tail}`;
}

export function getMenuIdByOrderType({
  settings,
  availableOrderTypes,
}: {
  settings: IRestaurantSettings | null;
  availableOrderTypes: IOrderType[] | null;
}): Record<OrderType, number | null> | null {
  const s = settings;
  /*const orderTypes = availableOrderTypes;

  if (!s || !orderTypes) return null;

  const idsByOrderType = {} as Record<OrderType, number | null>;

  if (orderTypes.some((t) => t.type === OrderType.Seat)) {
    idsByOrderType[OrderType.Seat] = s.menu1Id ?? null;
  }

  if (orderTypes.some((t) => t.type === OrderType.Away)) {
    idsByOrderType[OrderType.Away] = s.menu2Id ?? null;
  }

  if (orderTypes.some((t) => t.type === OrderType.Delivery)) {
    idsByOrderType[OrderType.Delivery] = s.menu3Id ?? null;
  }

  return idsByOrderType;*/

  if (!s || !s.multipleMenus) {
    return {
      [OrderType.Seat]: null,
      [OrderType.Away]: null,
      [OrderType.Delivery]: null,
    };
  }

  return {
    [OrderType.Seat]: s?.menu1Id ?? null,
    [OrderType.Away]: s?.menu2Id ?? null,
    [OrderType.Delivery]: s?.menu3Id ?? null,
  };
}

export function getAvailableMenus({
  menuIdByOrderType,
}: {
  menuIdByOrderType: ReturnType<typeof getMenuIdByOrderType>;
}) {
  /* Return an array of available menus, based in both the available order types and the menus available in the restaurant (according to menuIdByOrderType)
      ie.
      If all seat, away and delivery menuByOrderType are null, return a single menu ("Menu")
      If seat is null (or has one id), and away and delivery are set and have the same ID, return two menus ("Menu a la mesa" and "Menu para llevar y a domicilio")
      If seat is null (or has one id), and away and delivery are set but have different IDs, return three menus ("Menu para llevar", "Menu a domicilio" and "Menu a la mesa")
      */
  if (!menuIdByOrderType) return null;

  const typesByMenuId: Record<number | 'null', OrderType[]> = Object.entries(
    menuIdByOrderType
  ).reduce((acc, [type, _menuId]) => {
    const menuId: number | 'null' = _menuId !== null ? _menuId! : 'null';

    if (!acc[menuId]) acc[menuId] = [];
    acc[menuId].push(type as OrderType);
    return acc;
  }, {} as Record<number | 'null', OrderType[]>);

  const menuIds = Object.keys(typesByMenuId);
  const menuCount = menuIds.length;

  const menus: {
    id: number | null;
    title: string;
    types: OrderType[];
    index: number;
  }[] = [];

  if (menuCount === 1) {
    menus.push({
      id: menuIds[0] === 'null' ? null : parseInt(menuIds[0]),
      title: 'Por defecto',
      types: typesByMenuId[menuIds[0] as number | 'null'],
      index: 0,
    });
  } else {
    for (const [menuId, types] of Object.entries(typesByMenuId)) {
      const menuName = types
        .map((t) => ORDER_TYPES_MAP.get(t)!.title.toLowerCase())
        .join(' y ');

      menus.push({
        id: menuId === 'null' ? null : parseInt(menuId),
        title: menuName.slice(0, 1).toUpperCase() + menuName.slice(1),
        types,
        index: Math.min(...types.map((t) => ORDER_TYPES_MAP.get(t)!.index)),
      });
    }
  }

  // Sort by index asc
  menus.sort((a, b) => a.index - b.index);

  return menus;
}
